import type { JobStatusValue } from '../services/fingeringApi';

/** localStorage に進行中ジョブのライフサイクル状態をまとめて保存するキー。 */
export const JOB_LIFECYCLE_STORAGE_KEY = 'omakase-unshi:job-lifecycle';

/** async 化対象の job_type (full-async-migration-design §1.4)。 */
export type JobType =
  | 'from_pdf'
  | 'from_json_both'
  | 'from_json_pdf'
  | 'from_json_audio';

/**
 * 進行中ジョブ 1 件のライフサイクル状態。リロード後の復元に必要な情報を保持する。
 * params には元アクションのパラメータ (from_json_* の PendingAction 等) を任意で持たせる。
 */
/**
 * ジョブ投入時のパラメータ。リロード復元時に元ファイル名等を持ち越すために使う。
 * fileName は handleDone が saveFingeringResult の fileName に使用する (async reload F4)。
 */
export interface JobLifecycleParams {
  /** 結果 PDF のファイル名 (= 完成 PDF 名。 元タイトルではない)。 */
  fileName?: string;
  /**
   * issue #152: アップロード時の元タイトル (拡張子なし)。 完成 PDF / MP3 / omfz 名は
   * (元タイトル, part, mode) の純関数で組むため、 復元後の再生成でも命名ソースを保てるよう
   * fileName とは別に持ち越す。 旧 entry は欠落 (undefined) → fileName から derive で救済。
   */
  originalTitle?: string;
  /** 運指パート (from_pdf 投入時のみ)。 */
  part?: string;
  /** 出力モード (from_pdf 投入時のみ)。 */
  mode?: string;
  /** 元 PDF の byte サイズ (issue #76 Phase 1, 履歴 UI 表示用)。 */
  fileSizeBytes?: number;
  /** 変則チューニング指定 (issue #110, 再投入で元設定を忠実再現するため)。 */
  scordatura?: string;
  /** テンポ (issue #110, 再投入で元設定を忠実再現するため)。 */
  bpm?: number;
  // issue #113 案C で pageRange は write-dead のため削除 (選択は抽出済み PDF に反映され、再投入は
  // フォーム復元 = 全ページ)。旧 localStorage エントリの pageRange は余分フィールドとして無視される。
  /** 出力カラーモード (issue #110, 再投入で元設定を忠実再現するため)。 */
  colorMode?: string;
}

/** Drive 自動保存先フォルダ (issue #76 Phase 1, 完了時に Drive へ書き戻す用)。 */
export interface DriveSaveTarget {
  id: string;
  name: string;
}

export interface JobLifecycleEntry {
  jobId: string;
  jobType: JobType;
  status: JobStatusValue;
  createdAt: number;
  params?: JobLifecycleParams;
  /** ジョブ終了時刻 (issue #76 Phase 1)。done / failed / expired / error_processing で立てる。 */
  finishedAt?: number;
  /** エラー分類 (issue #76 Phase 1)。 'mxl_missing' / 'omr_timeout' 等を toast / 履歴文言に使う。 */
  errorKind?: string;
  /** エラー詳細メッセージ (issue #76 Phase 1)。toast / 履歴 UI で表示する。 */
  errorMessage?: string;
  /** Drive 自動保存先 (issue #76 Phase 1)。fire-and-forget 完了時の書き戻しに使う。 */
  driveSaveTarget?: DriveSaveTarget;
  /** 画面非表示中に done になった entry を画面復帰時に通知する mark (issue #76 Phase 4-β)。
   *  true: 未通知 (CompletionBanner 表示対象)、 false / undefined: 通知済 or 通知不要。 */
  notifyOnReturn?: boolean;
  /** issue #126: autoRestore が IndexedDB へ復元済みマーク。 再 mount 後の再復元 (再 getJobResult /
   *  再保存) を封じ、 元 PDF の破壊的上書きを防ぐ。 in-memory 去重 ref 喪失後も効く永続マーク。 */
  restored?: boolean;
}

/** failed entry の保持期間 (issue #76 Phase 1, listRecentlyFailedJobs の自動 prune 閾値)。 */
const FAILED_ENTRY_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

const ACTIVE_STATUSES: ReadonlySet<JobStatusValue> = new Set<JobStatusValue>([
  'queued',
  'processing',
]);

const FAILED_STATUSES: ReadonlySet<JobStatusValue> = new Set<JobStatusValue>([
  'failed',
  'expired',
  'error_processing',
]);

// issue #94 層2 (#36) 不変条件 #4: 終端状態。 ここに達した entry は markJobStatus の
// 中間遷移書き戻しで巻き戻さない (done + 失敗系)。
const TERMINAL_STATUSES: ReadonlySet<JobStatusValue> = new Set<JobStatusValue>([
  'done',
  'failed',
  'expired',
  'error_processing',
]);

type JobLifecycleMap = Record<string, JobLifecycleEntry>;

const JOB_TYPES: ReadonlySet<string> = new Set<JobType>([
  'from_pdf',
  'from_json_both',
  'from_json_pdf',
  'from_json_audio',
]);

function isValidEntry(value: unknown): value is JobLifecycleEntry {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Partial<JobLifecycleEntry>;
  if (
    typeof entry.jobId !== 'string' ||
    typeof entry.jobType !== 'string' ||
    !JOB_TYPES.has(entry.jobType) ||
    typeof entry.status !== 'string' ||
    typeof entry.createdAt !== 'number'
  ) {
    return false;
  }
  // 新 optional field の type guard (不在は OK、存在時は型一致を強制)
  if (entry.finishedAt !== undefined && typeof entry.finishedAt !== 'number') return false;
  if (entry.errorKind !== undefined && typeof entry.errorKind !== 'string') return false;
  if (entry.errorMessage !== undefined && typeof entry.errorMessage !== 'string') return false;
  if (entry.driveSaveTarget !== undefined) {
    const t = entry.driveSaveTarget;
    if (typeof t !== 'object' || t === null) return false;
    if (typeof t.id !== 'string' || typeof t.name !== 'string') return false;
  }
  if (entry.notifyOnReturn !== undefined && typeof entry.notifyOnReturn !== 'boolean') return false;
  if (entry.restored !== undefined && typeof entry.restored !== 'boolean') return false;
  // issue #110: params 拡張フィールドの type guard (不在は OK、存在時は型一致を強制)
  if (entry.params !== undefined) {
    const p = entry.params;
    if (typeof p !== 'object' || p === null) return false;
    if (p.scordatura !== undefined && typeof p.scordatura !== 'string') return false;
    if (p.bpm !== undefined && typeof p.bpm !== 'number') return false;
    if (p.colorMode !== undefined && typeof p.colorMode !== 'string') return false;
    // issue #152: 元タイトル (命名ソース)。 存在時は string を強制する。
    if (p.originalTitle !== undefined && typeof p.originalTitle !== 'string') return false;
  }
  return true;
}

function readMap(): JobLifecycleMap {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage.getItem(JOB_LIFECYCLE_STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const map: JobLifecycleMap = {};
    for (const [jobId, value] of Object.entries(parsed)) {
      if (isValidEntry(value)) {
        map[jobId] = value;
      }
    }
    return map;
  } catch {
    return {};
  }
}

function writeMap(map: JobLifecycleMap): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(JOB_LIFECYCLE_STORAGE_KEY, JSON.stringify(map));
}

/** ジョブのライフサイクル状態を保存する。同一 jobId は上書き更新。 */
export function saveJobLifecycleEntry(entry: JobLifecycleEntry): void {
  const map = readMap();
  map[entry.jobId] = entry;
  writeMap(map);
}

/** jobId でエントリを 1 件取得する。不存在のときは null。 */
export function loadJobLifecycleEntry(jobId: string): JobLifecycleEntry | null {
  const map = readMap();
  return map[jobId] ?? null;
}

/** jobId のエントリを 1 件削除する。不存在のときは何もしない。 */
export function clearJobLifecycleEntry(jobId: string): void {
  const map = readMap();
  if (jobId in map) {
    delete map[jobId];
    writeMap(map);
  }
}

/** 全エントリを削除する。 */
export function clearAllJobLifecycleEntries(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(JOB_LIFECYCLE_STORAGE_KEY);
}

/** 全エントリを createdAt 降順 (新しい順) で返す。 */
export function listJobLifecycleEntries(): JobLifecycleEntry[] {
  return Object.values(readMap()).sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * 進行中ジョブ (queued / processing) のみを createdAt 降順で返す (issue #76 Phase 1)。
 * done / failed / expired / error_processing は除外する。
 */
export function listActiveJobs(): JobLifecycleEntry[] {
  return Object.values(readMap())
    .filter((e) => ACTIVE_STATUSES.has(e.status))
    .sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * issue #95 (#28): 自動保存 ON の expired を「失敗」でなく「Drive 保存済みの可能性・要確認」として
 * 区別する errorKind。 jobTracker が ON expired 確定時にこの errorKind でマークし、 履歴 UI では
 * 赤の「直近失敗」でなく中立の「要確認」セクション + Drive 履歴誘導に出し分ける。
 */
export const EXPIRED_AUTOSAVED_ERROR_KIND = 'expired_autosaved';

/**
 * 最近失敗したジョブ (failed / expired / error_processing) を finishedAt 降順で返す (issue #76 Phase 1)。
 * finishedAt が {@link FAILED_ENTRY_RETENTION_MS} を超えた entry は storage から自動削除する (副作用)。
 * finishedAt が未定義の壊れた failed entry は除外する。
 * issue #95 (#28): errorKind='expired_autosaved' の ON expired は「失敗」でなく「要確認」扱いのため除外する
 * ({@link listNeedsDriveCheckJobs} が別途拾う)。
 */
export function listRecentlyFailedJobs(): JobLifecycleEntry[] {
  const map = readMap();
  const now = Date.now();
  let pruned = false;
  for (const [jobId, entry] of Object.entries(map)) {
    if (!FAILED_STATUSES.has(entry.status)) continue;
    if (typeof entry.finishedAt !== 'number') continue;
    if (now - entry.finishedAt > FAILED_ENTRY_RETENTION_MS) {
      delete map[jobId];
      pruned = true;
    }
  }
  if (pruned) writeMap(map);

  return Object.values(map)
    .filter(
      (e) =>
        FAILED_STATUSES.has(e.status) &&
        typeof e.finishedAt === 'number' &&
        e.errorKind !== EXPIRED_AUTOSAVED_ERROR_KIND,
    )
    .sort((a, b) => (b.finishedAt ?? 0) - (a.finishedAt ?? 0));
}

/**
 * issue #95 (#28): 自動保存 ON で expired 確定した「要確認 (Drive 保存済みの可能性)」entry を
 * finishedAt 降順で返す。 status='expired' かつ errorKind='expired_autosaved' のものだけ。
 * 履歴 UI の中立「要確認」セクションで Drive 履歴確認を促すために使う。
 */
export function listNeedsDriveCheckJobs(): JobLifecycleEntry[] {
  return Object.values(readMap())
    .filter(
      (e) =>
        e.status === 'expired' &&
        e.errorKind === EXPIRED_AUTOSAVED_ERROR_KIND &&
        typeof e.finishedAt === 'number',
    )
    .sort((a, b) => (b.finishedAt ?? 0) - (a.finishedAt ?? 0));
}

/** {@link markJobFinished} のオプション。エラー時に errorKind / errorMessage を渡す。 */
export interface MarkJobFinishedOpts {
  errorKind?: string;
  errorMessage?: string;
  /** 画面非表示中に done になった entry を画面復帰時に通知するマーク (issue #76 Phase 4-β)。 */
  notifyOnReturn?: boolean;
}

/**
 * ジョブ終了状態を上書きする (issue #76 Phase 1)。
 * 既存 entry の status / finishedAt / errorKind / errorMessage を更新して saveJobLifecycleEntry で書き戻す。
 * 未知 jobId は no-op。
 */
export function markJobFinished(
  jobId: string,
  status: JobStatusValue,
  opts: MarkJobFinishedOpts = {},
): void {
  const entry = loadJobLifecycleEntry(jobId);
  if (entry === null) return;
  const updated: JobLifecycleEntry = {
    ...entry,
    status,
    finishedAt: Date.now(),
    errorKind: opts.errorKind,
    errorMessage: opts.errorMessage,
    // issue #76 Phase 4-β: opts.notifyOnReturn=true なら画面復帰時バナー対象としてマーク。
    // 既存 entry の notifyOnReturn は opts 指定で上書き、 未指定なら維持。
    ...(opts.notifyOnReturn !== undefined ? { notifyOnReturn: opts.notifyOnReturn } : {}),
  };
  saveJobLifecycleEntry(updated);
}

/**
 * ジョブの中間遷移 (queued→processing) を entry.status へ書き戻す (issue #88)。
 * {@link markJobFinished} と異なり終端マーク (finishedAt) を立てず status のみ更新する。
 * 変化があれば saveJobLifecycleEntry して true、 無変化 (同値) / 未知 jobId は false を返す。
 * 戻り値で変化有無を判定し、 呼び出し側 (jobTracker poll) が無駄な broadcast を抑制できる。
 */
export function markJobStatus(jobId: string, status: JobStatusValue): boolean {
  const entry = loadJobLifecycleEntry(jobId);
  if (entry === null) return false;
  if (entry.status === status) return false;
  // issue #94 層2 (#36) 不変条件 #4: 終端 (done/failed/expired/error_processing) に達した entry は
  // 中間遷移書き戻しで巻き戻さない (終端済み job が再び active 扱いになる退行を防ぐ)。
  // markJobStatus の唯一の呼び出し元 jobTracker poll は終端で return 済みのため、 本ガードは
  // 既存挙動に影響しない (防御的な不変条件保証)。
  if (TERMINAL_STATUSES.has(entry.status)) return false;
  saveJobLifecycleEntry({ ...entry, status });
  return true;
}

/**
 * 画面復帰時に CompletionBanner で通知する対象 entry を返す (issue #76 Phase 4-β)。
 * notifyOnReturn=true かつ status='done' の entry のみを finishedAt 降順で返す。
 */
export function listEntriesNeedingReturnNotification(): JobLifecycleEntry[] {
  return Object.values(readMap())
    .filter((e) => e.status === 'done' && e.notifyOnReturn === true)
    .sort((a, b) => (b.finishedAt ?? 0) - (a.finishedAt ?? 0));
}

/**
 * 自動復元 (issue #85) の対象 done entry を返す。
 * status='done' かつ driveSaveTarget あり (= fire-and-forget) の entry を finishedAt 降順で返す。
 * notifyOnReturn は不問 (リアルタイム=false / キャッチアップ=true の双方を拾う)。
 */
export function listDoneEntriesForAutoRestore(): JobLifecycleEntry[] {
  return Object.values(readMap())
    .filter((e) => e.status === 'done' && e.driveSaveTarget !== undefined)
    .sort((a, b) => (b.finishedAt ?? 0) - (a.finishedAt ?? 0));
}

/**
 * バナー表示後の通知済マーク (issue #76 Phase 4-β)。 notifyOnReturn=false 化、 entry 自体は残す。
 * 未知 jobId は no-op。
 */
export function markEntryNotified(jobId: string): void {
  const entry = loadJobLifecycleEntry(jobId);
  if (entry === null) return;
  saveJobLifecycleEntry({ ...entry, notifyOnReturn: false });
}

/**
 * issue #126: autoRestore の IndexedDB 復元完了マーク。 entry.restored=true 化。 未知 jobId は no-op。
 * 再 mount で in-memory 去重 ref (autoRestoredRef) が消えても、 このマークで再復元 (再 getJobResult /
 * 再保存) を封じ、 元 PDF の破壊的上書きを防ぐ。
 */
export function markEntryRestored(jobId: string): void {
  const entry = loadJobLifecycleEntry(jobId);
  if (entry === null) return;
  saveJobLifecycleEntry({ ...entry, restored: true });
}

/**
 * ユーザが手動で失敗 entry を消す (issue #76 Phase 1)。
 * 現状は {@link clearJobLifecycleEntry} と同等だが、UI 側の意図を明示する別名として残す。
 */
export function dismissFailedJob(jobId: string): void {
  clearJobLifecycleEntry(jobId);
}

// =============================================================================
// pre-jobID 投入中マーカー (issue #9 reload race)
//
// POST /fingering/jobs のアップロード〜job_id 受領前の区間 (pre-jobID) で reload
// した場合、JobLifecycleEntry はまだ存在しないため mount hydration ガードが
// 「進行中 job なし」と誤判定し IndexedDB の前回 done 結果を表示してしまう。
// このマーカーで pre-jobID 区間を可視化し、hydration ガードに `entry なし
// AND マーカーなし` の追加条件を持たせる (spec 2026-05-20-fix-reload-race-design)。
// =============================================================================

/** pre-jobID 区間 (POST /fingering/jobs アップロード中) の投入中マーカー localStorage キー。 */
export const PENDING_SUBMIT_STORAGE_KEY = 'omakase-unshi:pending-submit';

/** 投入中マーカーの有効期限 (5 分)。pre-jobID 区間は本来数秒なので十分なマージン。 */
export const PENDING_SUBMIT_TTL_MS = 5 * 60 * 1000;

interface PendingSubmitMarker {
  createdAt: number;
}

function isValidPendingSubmitMarker(value: unknown): value is PendingSubmitMarker {
  if (typeof value !== 'object' || value === null) return false;
  const marker = value as Partial<PendingSubmitMarker>;
  return typeof marker.createdAt === 'number';
}

/** 投入中マーカーを localStorage に書く。POST 直前 (jobId 受領前) に呼ぶ。 */
export function savePendingSubmitMarker(): void {
  if (typeof window === 'undefined') return;
  const marker: PendingSubmitMarker = { createdAt: Date.now() };
  window.localStorage.setItem(PENDING_SUBMIT_STORAGE_KEY, JSON.stringify(marker));
}

/** 投入中マーカーを削除する。jobId 受領 (entry 書込後) または POST 失敗 finally で呼ぶ。 */
export function clearPendingSubmitMarker(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(PENDING_SUBMIT_STORAGE_KEY);
}

/**
 * 投入中マーカーが TTL 内に存在するかを同期的に返す。
 * TTL 切れ / 不正 JSON のときはマーカーを削除して false を返す (自己クリーンアップ)。
 */
export function hasPendingSubmitMarker(): boolean {
  if (typeof window === 'undefined') return false;
  const raw = window.localStorage.getItem(PENDING_SUBMIT_STORAGE_KEY);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isValidPendingSubmitMarker(parsed)) {
      window.localStorage.removeItem(PENDING_SUBMIT_STORAGE_KEY);
      return false;
    }
    if (Date.now() - parsed.createdAt > PENDING_SUBMIT_TTL_MS) {
      window.localStorage.removeItem(PENDING_SUBMIT_STORAGE_KEY);
      return false;
    }
    return true;
  } catch {
    window.localStorage.removeItem(PENDING_SUBMIT_STORAGE_KEY);
    return false;
  }
}
